import { Box, Button, ClickAwayListener, Grow, Icon, MenuItem, MenuList, Paper, Popper, Tooltip } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useLogoutMutation } from 'api/auth/auth.api'
import { useUser } from 'api/users/users.api'
import classNames from 'classnames'
import { getDisplayUserName, useHasGlobalPermission } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import sigholmLogo from '/img/sigholm_logo.png'
import { ProfileMenuLanguagePicker } from './components/LanguagePicker/LanguagePicker'
import ProfileMenuItem from './components/ProfileMenuItem/ProfileMenuItem'
import styles from './ProfileMenu.module.less'

const Wire = ({ children, ...props }: any): ReactElement => children(props)

export default function ProfileMenu(): ReactElement {
  const { t } = useTranslation()
  const { activeSystem, impersonatingUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false)
  const anchorEl = useRef<null | HTMLButtonElement>(null)
  const { mutateAsync } = useLogoutMutation()
  const hasViewUserAdminModuleAccess = useHasGlobalPermission(`view_user_admin_module`)

  function handleToggle(): void {
    setOpen(!open)
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Escape') {
      setOpen(false)
      setLanguageDropdownOpen(false)
    }
  }

  const { data: user, isPending } = useUser()
  const userName = isPending ? `` : getDisplayUserName(user, true)

  function handleClose(event: MouseEvent | TouchEvent | React.MouseEvent<HTMLLIElement>): void {
    if (anchorEl.current && anchorEl.current.contains(event.target as Node)) {
      return
    }

    setOpen(false)
    setLanguageDropdownOpen(false) // Also close language dropdown when profile menu closes
  }

  return (
    <>
      <Button
        color="inherit"
        variant="outlined"
        ref={anchorEl}
        aria-owns={open ? `simple-menu` : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={styles.ProfileMenu_ProfileButton}
        data-testid="profile-menu-button"
      >
        <Grid container alignItems="center" justifyContent="center" spacing={1}>
          <Grid>
            <Icon className={classNames(`fas fa-user-circle`, styles.ProfileMenu_LeftIcon)} />
          </Grid>
          <Grid>
            <div className={styles.ProfileMenu_UserName}>{userName ? userName : ``}</div>
          </Grid>
          <Grid>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <div className={styles.ProfileMenu_CustomerIcon}>
                {activeSystem && activeSystem?.organization_logo_url ? (
                  <img height="80%" src={activeSystem.organization_logo_url} alt="customer logo" />
                ) : null}
              </div>
            </Box>
          </Grid>
        </Grid>
      </Button>
      <Popper open={open} anchorEl={anchorEl.current} transition disablePortal>
        {({ TransitionProps, placement }): ReactElement => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === `bottom` ? `center top` : `center bottom`,
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocus disabledItemsFocusable={false} onKeyDown={handleKeyDown}>
                  <Wire>
                    {(props: object): ReactElement => (
                      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        <MenuItem
                          {...props}
                          onClick={handleClose}
                          className={styles.ProfileMenu_CustomerIconMenu}
                          tabIndex={0}
                        >
                          <img
                            height="20%"
                            width="20%"
                            src={
                              activeSystem && activeSystem?.organization_logo_url
                                ? activeSystem.organization_logo_url
                                : sigholmLogo
                            }
                            alt="customer logo"
                          />
                        </MenuItem>
                      </Box>
                    )}
                  </Wire>
                  <ProfileMenuItem icon="fal fa-user-cog" tabIndex={0}>
                    <Link className={styles.ProfileMenu_UserProfile} to="/userprofile">
                      {t(`Account settings`)}
                    </Link>
                  </ProfileMenuItem>

                  <ProfileMenuLanguagePicker
                    isOpen={languageDropdownOpen}
                    onToggle={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                    onClose={() => setLanguageDropdownOpen(false)}
                  />

                  {hasViewUserAdminModuleAccess && (
                    <ProfileMenuItem icon="fal fa-user-cog" tabIndex={0}>
                      <Link className={styles.ProfileMenu_UserProfile} to="/adminsettings">
                        {t(`Admin settings`)}
                      </Link>
                    </ProfileMenuItem>
                  )}

                  <Tooltip
                    title={impersonatingUser ? t('Stop impersonation first to logout') : ''}
                    placement="left"
                    disableHoverListener={!impersonatingUser}
                  >
                    <span style={{ display: 'block' }}>
                      <ProfileMenuItem
                        icon="fal fa-sign-out"
                        onClick={(): void => {
                          if (impersonatingUser) return

                          if (uiConfigStore.hasAnyPreviewDraft()) {
                            const shouldDiscardDrafts = window.confirm('Discard unsaved drafts and log out?')

                            if (!shouldDiscardDrafts) {
                              return
                            }

                            uiConfigStore.clearAllPreviewDrafts()
                          }

                          mutateAsync()
                        }}
                        disabled={!!impersonatingUser}
                        tabIndex={0}
                      >
                        {t(`Logout`)}
                      </ProfileMenuItem>
                    </span>
                  </Tooltip>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
